import { isEmpty } from "lodash";
import { MutableRefObject, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { isNodeLabelExists } from "../../../../../../components/drawing-tool/utils";
import { NodeLabelExistError } from "../../../../../../config/enums/NodeLabelError.enum";
import { errorView, successMessage } from "../../../../../../helpers/ErrorHandler";
import { INTERNAL_TRANSPORT_GATE_CONFIG } from "../internalTransportGate.config";
import {
  InternalTransportFormValues,
  InternalTransportGate,
  InternalTransportOriginalSnapshot,
  InternalTransportToolVariant,
  SaveDocumentDataHandle
} from "../internalTransport.types";
import {
  buildInternalTransportDeletePayload,
  buildInternalTransportSubmitPayload
} from "../internalTransport.utils";

interface UseInternalTransportActionsOptions {
  gate: InternalTransportGate;
  variant: InternalTransportToolVariant;
  gateId: string | null | undefined;
  internalTransportId: string | null;
  nodeItem: any;
  chartNodes: any[];
  chartEdges: any[];
  connectedEdges: any[];
  supportsNotes: boolean;
  existingTransportName: string;
  originalSnapshot: InternalTransportOriginalSnapshot | null;
  notesRef: MutableRefObject<SaveDocumentDataHandle | null>;
  form: Pick<
    UseFormReturn<InternalTransportFormValues>,
    "getValues" | "setFocus" | "setError" | "clearErrors"
  >;
  setNodeItem: (node: any) => void;
  setChartNodes: (nodes: any[]) => void;
  saveDrawing: (edges: any[], nodes: any[]) => void;
  deleteElements: (payload: { nodes: { id: string }[]; edges?: any[] }) => void;
  postInternalTransport: (payload: unknown, options: { onSuccess: (response: any) => void }) => void;
  putInternalTransport: (payload: unknown, options: { onSuccess: (response: any) => void }) => void;
  deleteInternalTransport: (payload: unknown, options: { onSuccess: () => void }) => void;
  setVisibleDeleteModal: (visible: boolean) => void;
}

export const useInternalTransportActions = ({
  gate,
  variant,
  gateId,
  internalTransportId,
  nodeItem,
  chartNodes,
  chartEdges,
  connectedEdges,
  supportsNotes,
  existingTransportName,
  originalSnapshot,
  notesRef,
  form,
  setNodeItem,
  setChartNodes,
  saveDrawing,
  deleteElements,
  postInternalTransport,
  putInternalTransport,
  deleteInternalTransport,
  setVisibleDeleteModal
}: UseInternalTransportActionsOptions) => {
  const gateConfig = INTERNAL_TRANSPORT_GATE_CONFIG[gate];

  const saveTransportNodeData = useCallback(
    async (savedData: any) => {
      try {
        if (supportsNotes) {
          await notesRef.current?.saveDocumentAndNote(savedData?._id);
        }
        const updatedNode = {
          ...nodeItem,
          data: {
            ...nodeItem?.data,
            label: `Internal Transport (${savedData?.internalTransportName})`,
            reference: {
              internalTransportId: savedData?._id
            }
          }
        };
        const updatedChartNodes = chartNodes.map((node: any) =>
          node.id === nodeItem?.id ? updatedNode : node
        );
        setChartNodes(updatedChartNodes);
        setNodeItem(updatedNode);
        saveDrawing(chartEdges, updatedChartNodes);
      } catch (error) {
        errorView(error);
      }
    },
    [
      supportsNotes,
      nodeItem,
      chartNodes,
      setChartNodes,
      setNodeItem,
      saveDrawing,
      chartEdges,
      notesRef
    ]
  );

  const onNodesDelete = useCallback(() => {
    notesRef.current?.deleteComment();
    if (variant === "mapping") {
      deleteElements({ nodes: [{ id: nodeItem.id }], edges: connectedEdges });
    } else {
      deleteElements({ nodes: [{ id: nodeItem.id }] });
    }
    setNodeItem(null);
  }, [connectedEdges, deleteElements, nodeItem, setNodeItem, variant, notesRef]);

  const onSubmit = useCallback(
    (formData: InternalTransportFormValues) => {
      const transportNameChanged = form.getValues("internalTransportName");
      if (
        existingTransportName !== transportNameChanged &&
        isNodeLabelExists(chartNodes as any, transportNameChanged)
      ) {
        form.clearErrors("internalTransportName");
        form.setFocus("internalTransportName");
        form.setError("internalTransportName", {
          type: "manual",
          message: NodeLabelExistError.INTERNAL_TRANSPORT
        });
        return;
      }

      if (isEmpty(formData.transportationType)) {
        errorView("Please select transportation type");
        return;
      }

      if (!gateId) {
        errorView(gateConfig.missingGateError);
        return;
      }

      const dataToSubmit = buildInternalTransportSubmitPayload({
        gate,
        gateId,
        internalTransportId,
        formData,
        defaultType: formData.transportationType,
        weightUnit: Number(formData.weightUnit),
        variant,
        originalName: originalSnapshot?.transportName
      });

      const onSuccess = (response: any) => {
        saveTransportNodeData(response);
        successMessage(
          internalTransportId
            ? "Internal Transport updated successfully"
            : "Internal Transport created successfully"
        );
      };

      if (internalTransportId) {
        putInternalTransport(dataToSubmit, { onSuccess });
        return;
      }

      postInternalTransport(dataToSubmit, { onSuccess });
    },
    [
      form,
      existingTransportName,
      chartNodes,
      gateId,
      gate,
      gateConfig.missingGateError,
      internalTransportId,
      variant,
      originalSnapshot?.transportName,
      putInternalTransport,
      postInternalTransport,
      saveTransportNodeData
    ]
  );

  const handleDelete = useCallback(() => {
    const dataToDelete = buildInternalTransportDeletePayload({
      gate,
      gateId,
      internalTransportId
    });

    if (nodeItem?.data?.reference) {
      deleteInternalTransport(dataToDelete, {
        onSuccess: () => {
          onNodesDelete();
          setVisibleDeleteModal(false);
          successMessage("Internal Transport deleted successfully");
        }
      });
      return;
    }

    onNodesDelete();
    setVisibleDeleteModal(false);
    successMessage("Internal Transport deleted successfully");
  }, [
    gate,
    gateId,
    internalTransportId,
    nodeItem?.data?.reference,
    deleteInternalTransport,
    onNodesDelete,
    setVisibleDeleteModal
  ]);

  return { onSubmit, handleDelete };
};
